import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives/responsive';

import { wacToken } from '@/libraries/design/foundations';

import type { JobTableProps } from '../types';

/**
 * One `<table>` per `JobGroup`, preceded by an `H3` when the group has a
 * label. Renders `emptyStateLabel` instead when every group is empty.
 */
export const JobTable = ({
	groups,
	positionColumnLabel,
	locationColumnLabel,
	emptyStateLabel,
}: JobTableProps) => {
	const hasResults = groups.some((group) => group.rows.length > 0);

	if (!hasResults) {
		return <p css={emptyStateStyle}>{emptyStateLabel}</p>;
	}

	return (
		<>
			{groups.map((group) => (
				<div key={group.key}>
					{group.label !== undefined && <h3 css={groupHeadingStyle}>{group.label}</h3>}
					<table css={tableStyle}>
						<thead>
							<tr>
								<th scope="col" css={thStyle}>
									{positionColumnLabel}
								</th>
								<th scope="col" css={thStyle}>
									{locationColumnLabel}
								</th>
							</tr>
						</thead>
						<tbody>
							{group.rows.map((row) => (
								<tr key={row.key} css={rowStyle}>
									<td css={tdStyle}>
										{row.href !== undefined ? (
											<a
												href={row.href}
												css={titleLinkStyle}
												data-event="clicked"
												data-event-component="link"
												data-event-container="careersGallery"
											>
												{row.title}
											</a>
										) : (
											<span css={titlePlainStyle}>{row.title}</span>
										)}
									</td>
									<td css={tdStyle}>{row.location}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			))}
		</>
	);
};

const groupHeadingStyle = css({
	fontFamily: wacToken('font.charlie.display'),
	fontSize: '24px',
	lineHeight: '32px',
	fontWeight: 500,
	letterSpacing: '0.3px',
	color: wacToken('color.extended.blue1000'),
	marginBlockEnd: '26px',
});

const tableStyle = css({
	width: '100%',
	borderCollapse: 'collapse',
	marginBlockEnd: '45px',
	tableLayout: 'auto',
	[media.above.sm]: {
		tableLayout: 'fixed',
	},
});

const thStyle = css({
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '16px',
	lineHeight: '16px',
	fontWeight: 600,
	letterSpacing: '1px',
	textTransform: 'uppercase',
	color: wacToken('color.primary.blue'),
	textAlign: 'left',
	padding: '20px 0',
	[media.above.sm]: {
		width: '50%',
	},
});

const rowStyle = css({
	borderBlockEnd: `1px solid ${wacToken('color.neutral.light200')}`,
});

const tdStyle = css({
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '16px',
	lineHeight: '16px',
	fontWeight: 400,
	color: wacToken('color.extended.blue1000'),
	padding: 0,
});

const titleLinkStyle = css({
	display: 'block',
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '16px',
	fontWeight: 400,
	color: wacToken('color.primary.blue'),
	textDecoration: 'underline',
	padding: '20px 20px 20px 0',
});

const titlePlainStyle = css({
	display: 'block',
	padding: '20px 20px 20px 0',
});

const emptyStateStyle = css({
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '16px',
	lineHeight: '24px',
	fontWeight: 400,
	color: wacToken('color.extended.blue1000'),
});
